// The hero kit on heroes (docs/145): every hero is drawn from the modular kit of docs/129, its species' body as
// the hero's sex, wearing the art of every piece it has on, in the hero's dye. One compositor serves every
// screen: it bakes a hero's two frames once per look into small canvases (cached), which React draws as
// <canvas> elements (`HeroFigure`) and Phaser adds as textures. The stacking, anchors, conforming, the head
// under a hat and the fist round a grip are docs/129 IA4–IA8, the same rules as the `?itemqa=1` dresser.
//
// The canvas is the 32 px hero canvas (docs/12 T1) with headroom above it for the tallest hats on the tallest
// heads (docs/129 §7): KIT_W × KIT_H, the 24 px board cell at (4, 5 + KIT_HEAD), feet on its bottom row.
//
// KH3, breathing: Oryx's heroes have a second frame in which everything above the ankles sits a pixel lower
// (the legs give the pixel back) and the feet stay put. The kit's second frame is made from the first the same
// way: every row above `BREATH_CUT` moves down one, the row at the cut is the one above it, the feet don't move.
import { BODIES_BY_SEX, BODY_Z, CONFORM, FIST_Z, ITEM_ART, type BodyArt, type Pose, type Sex, type Slot, type WornPart } from './itemArt';
import { HAT_BOTTOM, OUTER_OUTLINE, SILHOUETTE, type Runs } from './itemArtShapes';
import { DYES, ITEM_DYES } from './itemDyes.gen';
import { artDefOf } from './baseItemArt';
import { garbCut } from './starterGarb';

export type { Sex, Slot } from './itemArt';

/** What a hero wears, one catalogue piece per slot. */
export type Outfit = Readonly<Partial<Record<Slot, number>>>;

/** Everything the kit needs to draw a hero. */
export interface HeroLook {
  /** `fk_grammar::SpeciesId` code (`hero.species`). */
  species: number;
  sex: Sex;
  /** The hero's dye (`DYES` id); 0 is the art as drawn. */
  dye: number;
  outfit: Outfit;
  /** docs/156: a worn piece dyed on its own, by the slot it is drawn in (a slot not here is in `dye`). */
  dyes?: Readonly<Partial<Record<Slot, number>>>;
}

/** docs/156: the dye the piece in `slot` is drawn in: its own, else the hero's. */
export const dyeIn = (l: HeroLook, slot: Slot): number => l.dyes?.[slot] ?? l.dye;

export const KIT_W = 32;
/** Rows of headroom above the 32 px canvas: a tall hat on the birdfolk's head reaches two rows above it. */
export const KIT_HEAD = 4;
export const KIT_H = 32 + KIT_HEAD;
/** The board cell inside the kit canvas: a kit figure stands where a 24 px Oryx frame stood. */
export const KIT_CELL = { x: 4, y: 5 + KIT_HEAD, size: 24 } as const;
/** Where the feet stand: the bottom-centre of the board cell, as a fraction of the canvas (Phaser's origin). */
export const KIT_ORIGIN = { x: (KIT_CELL.x + KIT_CELL.size / 2) / KIT_W, y: (KIT_CELL.y + KIT_CELL.size) / KIT_H } as const;
/** The floor: nothing is drawn below the feet row (IA8), whatever its anchor. */
const FLOOR = 28;
/** KH3: art rows above this one sink a pixel in the second frame; this row and the feet below it stay. */
export const BREATH_CUT = 25;

/** `fk_grammar::SpeciesId` codes → kit species keys. */
const SPECIES_KEYS = ['human', 'dwarf', 'elf', 'halfling', 'orc', 'tiefling', 'dragonborn', 'half-elf', 'gnome', 'birdfolk'] as const;

/** The species' body as this sex (a code past the list draws the human). */
export function bodyOf(species: number, sex: Sex): BodyArt {
  const key = SPECIES_KEYS[species] ?? 'human';
  const bodies = BODIES_BY_SEX[sex];
  return bodies.find((b) => b.key === key) ?? bodies[0]!;
}

/** `GearSlot` codes (fk-grammar, the stored `item.equipped_slot`). */
export const GEAR_SLOT = { MainHand: 0, OffHand: 1, Torso: 2, Head: 3, Cape: 4, Trinket: 5, Hands: 6, Feet: 7, Neck: 8, Trinket2: 9, Legs: 10, Waist: 11 } as const;

/**
 * KH2: what a hero wears, from its equipped items. Every worn piece with kit art goes on its art's slot (a base
 * piece by its stand-in's art, KH4), and a weapon goes in the hand it is held in: the main hand, or the off hand
 * (`off_hand`, drawn from the front hand, docs/151). Nothing without worn art is drawn (rings, charms,
 * neckpieces). A bare slot is drawn bare: a hero's class clothes (docs/144) are items it wears like any other.
 */
export function outfitOf(items: Iterable<{ def: number; equippedSlot?: number | null }>): Outfit {
  const out: Partial<Record<Slot, number>> = {};
  for (const [slot, i] of Object.entries(wornBySlot(items)) as Array<[Slot, { def: number }]>) out[slot] = artDefOf(i.def);
  return out;
}

/**
 * docs/156: the slots whose piece is dyed on its own (`dyeOf` its item's dye; undefined: it has none, so it is in the
 * hero's), where that differs from the hero's dye `dye`. The same pieces in the same slots as `outfitOf`.
 */
export function outfitDyesOf<T extends { def: number; equippedSlot?: number | null }>(items: Iterable<T>, dyeOf: (item: T) => number | undefined, dye: number): Partial<Record<Slot, number>> {
  const out: Partial<Record<Slot, number>> = {};
  for (const [slot, i] of Object.entries(wornBySlot(items)) as Array<[Slot, T]>) {
    const own = dyeOf(i);
    if (own !== undefined && own !== dye) out[slot] = own;
  }
  return out;
}

/** KH2: the item drawn in each slot (see `outfitOf`). */
function wornBySlot<T extends { def: number; equippedSlot?: number | null }>(items: Iterable<T>): Partial<Record<Slot, T>> {
  const out: Partial<Record<Slot, T>> = {};
  const worn = [...items].filter((i) => i.equippedSlot !== undefined && i.equippedSlot !== null);
  // the main hand first, so it keeps its weapon
  worn.sort((a, b) => (a.equippedSlot === GEAR_SLOT.MainHand ? -1 : 0) - (b.equippedSlot === GEAR_SLOT.MainHand ? -1 : 0));
  for (const i of worn) {
    const def = artDefOf(i.def);
    const art = ITEM_ART[def]?.worn?.slot;
    const slot = art === 'main_hand' ? handSlot(i.equippedSlot) : art;
    if (!slot || out[slot] !== undefined) continue;
    out[slot] = i;
  }
  return out;
}

/** The slot a weapon is drawn in, by the hand it is equipped in (a weapon in neither hand is not drawn). */
const handSlot = (equipped: number | null | undefined): Slot | undefined =>
  equipped === GEAR_SLOT.MainHand ? 'main_hand' : equipped === GEAR_SLOT.OffHand ? 'off_hand' : undefined;

/** A stable key for a look: two heroes who look alike share their baked frames. */
export function lookKey(l: HeroLook): string {
  const body = bodyOf(l.species, l.sex);
  const known = (d: number) => (DYES.some((r) => r.id === d) ? d : 0);
  const worn = (Object.keys(l.outfit) as Slot[]).sort().map((s) => `${s}=${l.outfit[s]}${known(dyeIn(l, s)) !== known(l.dye) ? `~${known(dyeIn(l, s))}` : ''}`).join(',');
  return `${body.key}.${l.sex}.${known(l.dye)}.${worn}`;
}

// ---- the stack ------------------------------------------------------------------------------------------------

type Keep = (x: number, y: number) => boolean;
interface Layer { url: string; z: number; at: readonly [number, number]; keep?: Keep; def?: number; dye?: number; layer: string; posed?: { pose: Pose; grip: readonly [number, number] }; behindHead?: boolean }

const runSet = (runs: Runs) => new Set(runs.flatMap(([y, x0, x1]) => Array.from({ length: x1 - x0 + 1 }, (_, i) => (x0 + i) * 32 + y)));
const SIL = { short: runSet(SILHOUETTE.short), medium: runSet(SILHOUETTE.medium), tall: runSet(SILHOUETTE.tall) };
const LINE = { short: runSet(OUTER_OUTLINE.short), medium: runSet(OUTER_OUTLINE.medium), tall: runSet(OUTER_OUTLINE.tall) };
const HEAD_LAYERS = new Set(['body', 'head', 'ears', 'face', 'tusks', 'hair']);
/** docs/158 HB1: the layers drawn again, down to the chin, over a piece held behind the head. */
const HEAD_OVER = new Set([...HEAD_LAYERS, 'beard', 'snout', 'horns', 'helmet']);

/** A held part's pose: in the front hand its front pose (docs/157 HP9) if it has one, else its species' own
 *  (`bySpecies`, docs/151) or the piece's. */
const poseOf = (p: WornPart & { front?: boolean }, species: string): Pose | undefined =>
  (p.front && p.frontPose ? p.frontPose : p.bySpecies && species in p.bySpecies ? p.bySpecies[species] : p.pose);

/**
 * The layers of a look, lowest z first, each with its offset and what it may show (docs/129 IA4–IA8). `held` says
 * which fists close round a grip: the back hand's (a weapon in the main hand) and the front hand's (one in the off hand).
 */
export function stackOf(look: HeroLook): { layers: Layer[]; hand: Layer[]; fist: ReadonlySet<number>; offFist: ReadonlySet<number>; held: { back: boolean; front: boolean }; body: BodyArt } {
  // docs/158: a piece in the front hand, or one marked behindHead, is drawn behind the head (HB1); one held across the
  // body in the back hand (a bow) has the whole back arm drawn over it, not only the fist (HB3)
  const body = bodyOf(look.species, look.sex);
  // a weapon in the off hand is drawn in the front hand, and so is a short head-forward one while the off hand holds
  // nothing drawn (docs/157 HP7); every other worn part is drawn where its own layer puts it
  const free = !look.outfit.off_hand || !ITEM_ART[look.outfit.off_hand]?.worn;
  const parts: Array<WornPart & { def: number; dye: number; front: boolean }> = (Object.entries(look.outfit) as Array<[Slot, number | undefined]>).flatMap(([slot, def]) =>
    def ? (ITEM_ART[def]?.worn?.parts ?? []).map((p) => ({ ...p, def, dye: dyeIn(look, slot), front: p.layer === 'main_hand' && (slot === 'off_hand' || (free && !!p.frontWhenFree)) })) : []);
  // HP6: a lifted piece in the back hand is drawn `lift` rows higher (held further down the haft)
  const at = (p: WornPart & { front: boolean }): readonly [number, number] => {
    if (!p.grip) return body.anchors[p.layer] ?? [0, 0];
    const hand = p.front ? body.offHand : body.hand;
    return [hand[0] - p.grip[0], hand[1] - p.grip[1] - (p.front ? 0 : poseOf(p, body.key)?.lift ?? 0)];
  };
  // IA5: a body-hugging piece shows inside the wearer's outline, under its outer edge, plus what it drew outside the human's
  const sil = SIL[body.height], line = LINE[body.height];
  const conform = (o: readonly [number, number]): Keep => (x, y) => (sil.has(x * 32 + y) && !line.has(x * 32 + y)) || !SIL.medium.has((x - o[0]) * 32 + (y - o[1]));
  // IA6: under a hat the head shows nothing above the hat's lowest pixel in its columns (a closed helm: anywhere)
  const hats = parts.filter((p) => p.layer === 'helmet' && p.cover !== 'band' && p.file && HAT_BOTTOM[p.file]);
  const underHat: Keep | undefined = hats.length === 0 ? undefined : (x, y) => hats.every((h) => {
    const [dx, dy] = at(h), bottom = HAT_BOTTOM[h.file!]!;
    if (h.cover === 'closed') return y > Math.max(...bottom) + dy;
    const b = bottom[x - dx] ?? -1;
    return b < 0 || y > b + dy;
  });
  const layers: Layer[] = [
    ...parts.map((p) => {
      const o = at(p), pose = poseOf(p, body.key);
      return { layer: p.layer, url: p.url, z: p.z, at: o, def: p.def, dye: p.dye, keep: CONFORM.has(p.layer) ? conform(o) : undefined, posed: p.grip && pose ? { pose, grip: p.grip } : undefined, behindHead: !!p.grip && (p.front || !!p.behindHead) };
    }),
    ...body.features.map((f) => ({ layer: f.key, url: f.url, z: f.z, at: [0, 0] as const, keep: HEAD_LAYERS.has(f.key) ? underHat : undefined })),
    { layer: 'body', url: body.url, z: BODY_Z, at: [0, 0] as const, keep: underHat },
  ].sort((a, b) => a.z - b.z);
  // IA7: the hand is the body and whatever is worn on it (gloves sit at 50–55), drawn again over a grip, back or front
  const held = { back: parts.some((p) => p.grip && !p.front), front: parts.some((p) => p.grip && p.front) };
  const hand = held.back || held.front ? layers.filter((p) => p.z >= BODY_Z && p.z <= 55) : [];
  const cells = (f: BodyArt['fist']) => new Set(f.flatMap(([y, x0, x1]) => Array.from({ length: x1 - x0 + 1 }, (_, i) => (x0 + i) * 32 + y)));
  const arm = parts.some((p) => p.grip && !p.front && p.arm);
  return { layers, hand, fist: cells(arm ? body.arm : body.fist), offFist: cells(body.offFist), held, body };
}

// ---- pixels ---------------------------------------------------------------------------------------------------

/** A layer's art: 32×32 straight RGBA. */
export interface Pixels { w: number; h: number; data: Uint8ClampedArray }

const hex2 = (n: number) => n.toString(16).padStart(2, '0');
const rgbHex = (r: number, g: number, b: number) => hex2(r) + hex2(g) + hex2(b);
const parseHex = (h: string): [number, number, number] => [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];

/** IA12: a piece's colour → the hero's dye shade, for the colours of it that take the dye (undefined: none do). */
export function dyeMapOf(def: number | undefined, dye: number): Map<string, [number, number, number]> | undefined {
  if (def === undefined || !dye) return undefined;
  const ramp = DYES.find((d) => d.id === dye)?.ramp;
  const shades = ITEM_DYES[def];
  if (!ramp || !shades) return undefined;
  return new Map(Object.entries(shades).map(([from, i]) => [from.toLowerCase(), parseHex(ramp[Math.max(0, Math.min(3, i))]!)]));
}

/**
 * A held piece turned by its pose about its grip (docs/151): mirrored across the grip's column, then turned clockwise
 * a quarter at a time. Quarter turns and mirrors map pixels onto pixels, so this is exact; each destination pixel
 * takes the source pixel that the pose carries onto it.
 */
export function posed(px: Pixels, pose: Pose, grip: readonly [number, number]): Pixels {
  const [gx, gy] = grip;
  const data = new Uint8ClampedArray(px.data.length);
  for (let y = 0; y < px.h; y++) for (let x = 0; x < px.w; x++) {
    let dx = x - gx, dy = y - gy;
    for (let t = 0; t < (pose.turn ?? 0); t++) [dx, dy] = [dy, -dx];   // undo a clockwise quarter turn
    if (pose.flip) dx = -dx;                                           // undo the mirror
    const sx = gx + dx, sy = gy + dy;
    if (sx < 0 || sx >= px.w || sy < 0 || sy >= px.h) continue;
    data.set(px.data.subarray((sy * px.w + sx) * 4, (sy * px.w + sx) * 4 + 4), (y * px.w + x) * 4);
  }
  return { w: px.w, h: px.h, data };
}

/**
 * Both frames of a look from its layers' pixels (looked up by url): frame A is the stack, frame B is A breathing
 * (KH3). Pure, so the page and tests share it. Returns straight RGBA KIT_W × KIT_H.
 */
export function composePixels(look: HeroLook, art: (url: string) => Pixels | undefined): { a: Uint8ClampedArray; b: Uint8ClampedArray } {
  const { layers, hand, fist, offFist, held, body } = stackOf(look);
  const out = new Uint8ClampedArray(KIT_W * KIT_H * 4);
  /** Draws a layer (only on `only`'s cells, and where `where` holds, if given); returns the pixels it drew on, as
   *  indices into the output. */
  const draw = (l: Layer, only?: ReadonlySet<number>, where?: (x: number, y: number) => boolean): Set<number> => {
    const drawn = new Set<number>();
    const src = art(l.url);
    const px = src && l.posed ? posed(src, l.posed.pose, l.posed.grip) : src;
    if (!px) return drawn;
    const dyed = dyeMapOf(l.def, l.dye ?? look.dye);
    for (let sy = 0; sy < px.h; sy++) {
      const y = sy + l.at[1];
      if (y > FLOOR || y < -KIT_HEAD) continue;
      for (let sx = 0; sx < px.w; sx++) {
        const x = sx + l.at[0];
        if (x < 0 || x >= KIT_W) continue;
        const si = (sy * px.w + sx) * 4;
        const sa = px.data[si + 3]!;
        if (sa === 0) continue;
        if (only && !only.has(x * 32 + y)) continue;
        if (where && !where(x, y)) continue;
        // a clipped layer shows only on the 32 px canvas its masks are measured on (as the dresser draws it)
        if (l.keep && (y < 0 || !l.keep(x, y))) continue;
        let r = px.data[si]!, g = px.data[si + 1]!, b = px.data[si + 2]!;
        const to = dyed?.get(rgbHex(r, g, b));
        if (to) [r, g, b] = to;
        const di = ((y + KIT_HEAD) * KIT_W + x) * 4;
        drawn.add((y + KIT_HEAD) * KIT_W + x);
        if (sa === 255) { out[di] = r; out[di + 1] = g; out[di + 2] = b; out[di + 3] = 255; continue; }
        // source-over, straight alpha (the bust hint is translucent)
        const a = sa / 255, da = out[di + 3]! / 255, oa = a + da * (1 - a);
        out[di] = (r * a + out[di]! * da * (1 - a)) / oa;
        out[di + 1] = (g * a + out[di + 1]! * da * (1 - a)) / oa;
        out[di + 2] = (b * a + out[di + 2]! * da * (1 - a)) / oa;
        out[di + 3] = oa * 255;
      }
    }
    return drawn;
  };
  const headOver = layers.filter((l) => HEAD_OVER.has(l.layer));
  for (const l of layers) {
    if (l.z >= FIST_Z) continue;
    const drawn = draw(l);
    if (!l.behindHead) continue;
    // HB1: the head, every head layer down to the chin, drawn again where the piece is
    const under = (x: number, y: number) => y <= body.chin && drawn.has((y + KIT_HEAD) * KIT_W + x);
    for (const h of headOver) if (h.z < l.z) draw(h, undefined, under);
  }
  if (held.back) for (const l of hand) draw(l, fist);
  if (held.front) for (const l of hand) draw(l, offFist);
  for (const l of layers) if (l.z >= FIST_Z) draw(l);
  return { a: out, b: breathe(out) };
}

/** KH3: the second frame: every row above the cut a pixel lower; the feet stay. */
export function breathe(a: Uint8ClampedArray): Uint8ClampedArray {
  const b = new Uint8ClampedArray(a.length);
  const row = KIT_W * 4, cut = BREATH_CUT + KIT_HEAD;
  for (let y = 0; y < KIT_H; y++) {
    const from = y <= cut ? y - 1 : y;
    if (from >= 0) b.set(a.subarray(from * row, from * row + row), y * row);
  }
  return b;
}

// ---- the browser: images in, canvases out ----------------------------------------------------------------------

const art = new Map<string, Pixels | null>();
const loading = new Map<string, Promise<void>>();

function loadArt(url: string): Promise<void> {
  if (art.has(url)) return Promise.resolve();
  let p = loading.get(url);
  if (!p) {
    p = new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        const ctx = c.getContext('2d', { willReadFrequently: true });
        if (!ctx) { art.set(url, null); resolve(); return; }
        ctx.drawImage(img, 0, 0);
        art.set(url, { w: c.width, h: c.height, data: ctx.getImageData(0, 0, c.width, c.height).data });
        resolve();
      };
      img.onerror = () => { art.set(url, null); resolve(); };
      img.src = url;
    });
    loading.set(url, p);
  }
  return p;
}

/** A look baked: its key and its two frames. */
export interface HeroFrames { key: string; a: HTMLCanvasElement; b: HTMLCanvasElement; urlA: string; urlB: string }

const baked = new Map<string, HeroFrames>();
const baking = new Map<string, Promise<HeroFrames>>();
const listeners = new Set<() => void>();

const toCanvas = (px: Uint8ClampedArray): HTMLCanvasElement => {
  const c = document.createElement('canvas');
  c.width = KIT_W; c.height = KIT_H;
  c.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(px), KIT_W, KIT_H), 0, 0);
  return c;
};

/** A look's frames if they are baked already (canvas painters draw from this and redraw on `onBaked`). */
export function bakedHero(look: HeroLook): HeroFrames | undefined {
  return baked.get(lookKey(look));
}

/** Bakes a look's frames (once; later calls share the work). */
export function bakeHero(look: HeroLook): Promise<HeroFrames> {
  const key = lookKey(look);
  const done = baked.get(key);
  if (done) return Promise.resolve(done);
  let p = baking.get(key);
  if (!p) {
    const { layers } = stackOf(look);
    p = Promise.all([...new Set(layers.map((l) => l.url))].map(loadArt)).then(() => {
      const { a, b } = composePixels(look, (u) => art.get(u) ?? undefined);
      const ca = toCanvas(a), cb = toCanvas(b);
      const frames = { key, a: ca, b: cb, urlA: ca.toDataURL(), urlB: cb.toDataURL() };
      baked.set(key, frames);
      baking.delete(key);
      for (const f of listeners) f();
      return frames;
    });
    baking.set(key, p);
  }
  return p;
}

/** Calls `f` whenever another look finishes baking; returns the unsubscribe. */
export function onBaked(f: () => void): () => void {
  listeners.add(f);
  return () => { listeners.delete(f); };
}

/** The bare look of a species (nothing worn, no dye): what a hero with nothing known about it is drawn as. */
export const bareLook = (species = 0, sex: Sex = 'male'): HeroLook => ({ species, sex, dye: 0, outfit: {} });

/** KH5: what a hero of a class is drawn in when nothing else is known of it (a stand-in in a story harness, a
 *  friend's hero whose gear this client can't see): the class's starter kit (fk-grammar `starter_kit`, base
 *  weapons, KH4) in its own starter clothes (docs/144: its coat, legs, feet, first head, back and waist). */
const CLASS_KIT: Readonly<Record<string, readonly number[]>> = {
  fighter: [100, 110],
  rogue: [103],
  wizard: [107],
  cleric: [102, 111],
  ranger: [105],
  paladin: [100, 111],
  druid: [107],
  bard: [103],
};
const KIT_SLOTS: Readonly<Record<number, number>> = { 100: 0, 102: 0, 103: 0, 105: 0, 107: 0, 110: 1, 111: 1 };
const GARB_SLOTS: ReadonlyArray<readonly [unit: number, slot: number]> = [
  [0, GEAR_SLOT.Torso], [1, GEAR_SLOT.Legs], [2, GEAR_SLOT.Feet], [3, GEAR_SLOT.Head], [4, GEAR_SLOT.Cape], [5, GEAR_SLOT.Waist],
];

export function classLook(classId: string | undefined, species = 0, sex: Sex = 'male', dye = 0): HeroLook {
  const c = classId && CLASS_KIT[classId] ? classId : 'fighter';
  const ci = Object.keys(CLASS_KIT).indexOf(c);
  const kit = CLASS_KIT[c]!.map((def) => ({ def, equippedSlot: KIT_SLOTS[def] ?? null }));
  const clothes = GARB_SLOTS.flatMap(([unit, equippedSlot]) => {
    const def = garbCut(ci, unit);
    return def === undefined ? [] : [{ def, equippedSlot }];
  });
  return { species, sex, dye, outfit: outfitOf([...kit, ...clothes]) };
}

// ---- figures painted into a scene by number -------------------------------------------------------------------

/** Scenes that cast figures by Oryx creature frame (the story backdrops) cast a hero by a token at or past this. */
export const HERO_TOKEN = 1_000_000;
const tokens: HeroLook[] = [];
const tokenOf = new Map<string, number>();

/** A number standing for a look where a creature frame is expected; the painter draws the look's frames for it. */
export function heroToken(look: HeroLook): number {
  const k = lookKey(look);
  let n = tokenOf.get(k);
  if (n === undefined) { n = HERO_TOKEN + tokens.length; tokens.push(look); tokenOf.set(k, n); void bakeHero(look); }
  return n;
}

/** The look a token stands for. */
export const tokenLook = (n: number): HeroLook | undefined => (n >= HERO_TOKEN ? tokens[n - HERO_TOKEN] : undefined);

/**
 * Paints a look's frame onto a 2D canvas with its board cell's top-left at (`x`, `y`) (canvas px, `scale` canvas
 * px to an art px), mirrored to face left when `flip`. Returns false (and paints nothing) until the look is baked;
 * a painter repaints on `onBaked`.
 */
export function paintHero(ctx: CanvasRenderingContext2D, look: HeroLook, frame: 0 | 1, x: number, y: number, flip = false, scale = 1): boolean {
  const f = bakedHero(look);
  if (!f) { void bakeHero(look); return false; }
  const src = frame ? f.b : f.a;
  const w = KIT_W * scale, h = KIT_H * scale;
  const px = x - KIT_CELL.x * scale, py = y - KIT_CELL.y * scale;
  if (!flip) { ctx.drawImage(src, px, py, w, h); return true; }
  ctx.save(); ctx.translate(px + w, py); ctx.scale(-1, 1);
  ctx.drawImage(src, 0, 0, w, h);
  ctx.restore();
  return true;
}

/** Every look baked so far (the board adds each as a texture). */
export const allBaked = (): Iterable<HeroFrames> => baked.values();
